// Mirrors collaborative/collaborative_react/src/utils/canvasFirebase.js — same "session"
// path shape, same key sanitizing, same runTransaction-based admin start action. No Firebase
// Authentication here, same as that reference.
import { ref, onValue, set, get, runTransaction } from 'firebase/database';
import { getDatabase } from 'firebase/database';
import { getFirebaseApp } from '../firebase';

/** Firebase RTDB keys cannot contain . # $ [ ] / */
export function sanitizeFirebaseKey(value) {
  return String(value ?? 'unknown').replace(/[.#$/[\]]/g, '_');
}

/** Same org + session → same live hunt for every team and the admin. */
export function buildFirebaseSessionId(organizationId, sessionId) {
  const org = sanitizeFirebaseKey(organizationId || 'default');
  const sess = sanitizeFirebaseKey(sessionId);
  return `${org}__${sess}`;
}

function realtimeDatabase() {
  const app = getFirebaseApp();
  return app ? getDatabase(app) : null;
}

// ---- Hunt start signal ----
// session/{firebaseSessionId}/huntStart = { startedAt, resetNonce }
// startedAt is only ever set by an explicit admin action (Publish or Live Host's "Start hunt"
// below) — teams in the Waiting Room never seed it themselves, so they stay on "waiting for
// host to start" until the admin flips it for everyone.

export function huntStartRef(organizationId, sessionId) {
  const database = realtimeDatabase();
  if (!database) return null;
  const fbSessionId = buildFirebaseSessionId(organizationId, sessionId);
  return ref(database, `session/${fbSessionId}/huntStart`);
}

/** Admin action: go live for every team waiting on this event. */
export async function startHunt(organizationId, sessionId) {
  const r = huntStartRef(organizationId, sessionId);
  if (!r) throw new Error('Firebase is not configured');
  await runTransaction(r, (current) => ({
    startedAt: Date.now(),
    resetNonce: (current?.resetNonce || 0) + 1,
  }));
}

export function subscribeHuntStart(organizationId, sessionId, onData) {
  const r = huntStartRef(organizationId, sessionId);
  if (!r) {
    onData(null);
    return () => {};
  }
  return onValue(r, (snapshot) => {
    onData(snapshot.val() || null);
  });
}

// ---- Waiting Room presence ----
// session/{firebaseSessionId}/users/{firebaseUserId} = { userId, teamNumber, teamName, status, joinedAt }
// Mirrors collaborative's registerUserCanvasSession/subscribeSessionUsers — lets the admin (and
// the "X of Y teams in" counter) see live who's actually sitting in the Waiting Room.

export function sessionUsersRef(organizationId, sessionId) {
  const database = realtimeDatabase();
  if (!database) return null;
  const fbSessionId = buildFirebaseSessionId(organizationId, sessionId);
  return ref(database, `session/${fbSessionId}/users`);
}

export function userPresenceRef(organizationId, sessionId, userId) {
  const database = realtimeDatabase();
  if (!database) return null;
  const fbSessionId = buildFirebaseSessionId(organizationId, sessionId);
  return ref(database, `session/${fbSessionId}/users/${sanitizeFirebaseKey(userId)}`);
}

/** Seed (or refresh) this player's presence node in the Waiting Room. */
export async function registerHuntPlayer(organizationId, sessionId, userId, payload) {
  const r = userPresenceRef(organizationId, sessionId, userId);
  if (!r) return;
  const snap = await get(r);
  const current = snap.exists() ? snap.val() : {};
  await set(r, {
    status: 'waiting',
    ...current,
    ...payload,
    updatedAt: Date.now(),
  });
}

export function subscribeHuntPlayers(organizationId, sessionId, onData) {
  const r = sessionUsersRef(organizationId, sessionId);
  if (!r) {
    onData({});
    return () => {};
  }
  return onValue(r, (snapshot) => {
    onData(snapshot.val() || {});
  });
}
